import * as THREE from 'three';

const clamp = THREE.MathUtils.clamp;
/** Short eyelid closure every 4.7 seconds; no random per-frame state. */
export function blinkAmount(seconds: number): number {
  const phase = ((seconds % 4.7) + 4.7) % 4.7;
  return phase < .18 ? Math.sin(Math.PI * phase / .18) ** 2 : 0;
}
export function gazeAngle(x: number, z: number, distance: number): number {
  return distance <= 3.5 && z > .1 ? clamp(Math.atan2(x, z), -.28, .28) : 0;
}
type Joint = { bone: THREE.Object3D; rest: THREE.Quaternion; position: THREE.Vector3; lift: THREE.Vector3; x: THREE.Vector3; y: THREE.Vector3 };

/** Additive, bounded motion about the imported rest pose. Never moves feet or collision. */
export class CharacterMotion {
  private readonly joints = new Map<string, Joint>();
  private time = 1;
  private nodTime = 10;
  private yaw = 0;
  private lastLine = '';
  private readonly localCamera = new THREE.Vector3();
  private readonly origin = new THREE.Vector3();
  private readonly turn = new THREE.Quaternion();
  private readonly tilt = new THREE.Quaternion();

  constructor(root: THREE.Object3D, private readonly slot: THREE.Object3D) {
    root.updateWorldMatrix(true, true);
    const facingFrame = slot.getWorldQuaternion(new THREE.Quaternion());
    for (const name of ['head', 'spine02', 'eye.L', 'eye.R', 'orbicularis03.L', 'orbicularis03.R', 'orbicularis04.L', 'orbicularis04.R']) {
      // GLTFLoader sanitizes dots for animation-track paths; use the same helper.
      const bone = root.getObjectByName(THREE.PropertyBinding.sanitizeNodeName(name)) ?? root.getObjectByName(name);
      if (!bone) continue;
      const inverse = bone.getWorldQuaternion(new THREE.Quaternion()).invert();
      const parentInverse = bone.parent!.matrixWorld.clone().invert();
      const lift = new THREE.Vector3(0, 1, 0).applyMatrix4(parentInverse)
        .sub(new THREE.Vector3().applyMatrix4(parentInverse));
      this.joints.set(name, { bone, rest: bone.quaternion.clone(), position: bone.position.clone(), lift,
        x: new THREE.Vector3(1, 0, 0).applyQuaternion(facingFrame).applyQuaternion(inverse),
        y: new THREE.Vector3(0, 1, 0).applyQuaternion(facingFrame).applyQuaternion(inverse) });
    }
  }

  dialogue(speaker: string | null, lineKey: string): void {
    if (speaker === '高瀬' && lineKey !== this.lastLine) this.nodTime = 0;
    this.lastLine = lineKey;
  }

  /** Restore imported rest pose for reproducible portrait comparisons. */
  reset(): void {
    this.time = 1; this.nodTime = 10; this.yaw = 0; this.lastLine = '';
    for (const joint of this.joints.values()) {
      joint.bone.quaternion.copy(joint.rest);
      joint.bone.position.copy(joint.position);
    }
  }

  update(delta: number, camera: THREE.Camera, blinkOverride?: number): void {
    const dt = Number.isFinite(delta) ? clamp(delta, 0, .05) : 0;
    this.time += dt;
    this.nodTime += dt;
    camera.getWorldPosition(this.localCamera);
    this.slot.getWorldPosition(this.origin);
    const distance = this.localCamera.distanceTo(this.origin);
    this.slot.worldToLocal(this.localCamera);
    const target = gazeAngle(this.localCamera.x, this.localCamera.z, distance);
    this.yaw += (target - this.yaw) * (1 - Math.exp(-dt * 3));
    const blink = blinkOverride === undefined ? blinkAmount(this.time) : clamp(blinkOverride, 0, 1);
    const nod = this.nodTime < 1.1 ? .065 * Math.sin(Math.PI * this.nodTime / 1.1) ** 2 : 0;
    this.pose('head', nod, this.yaw * .8);
    this.pose('spine02', .004 * Math.sin(this.time * 1.5), 0);
    for (const side of ['L', 'R']) {
      this.pose(`eye.${side}`, 0, this.yaw * .2);
      this.pose(`orbicularis03.${side}`, 0, 0, -blink * .012);
      this.pose(`orbicularis04.${side}`, 0, 0, blink * .003);
    }
  }

  private pose(name: string, x: number, y: number, lift = 0): void {
    const joint = this.joints.get(name);
    if (!joint) return;
    this.turn.setFromAxisAngle(joint.y, y);
    this.tilt.setFromAxisAngle(joint.x, x);
    joint.bone.quaternion.copy(joint.rest).multiply(this.turn).multiply(this.tilt);
    joint.bone.position.copy(joint.position).addScaledVector(joint.lift, lift);
  }
}
